import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import ArrowDownIcon from '@icons/16/arrow-down-layta.svg';

import styles from './Accordion.module.scss';
import { AccordionButton } from './AccordionButton';
import { AccordionHeading } from './AccordionHeading';
import { AccordionItem } from './AccordionItem';
import { AccordionPanel } from './AccordionPanel';
import { AccordionContext } from './context';
import { AccordionProps, AccordionVariant } from './types';

const resolveVariant = (theme?: string, variant?: AccordionVariant): AccordionVariant => {
    if (variant) {
        return variant;
    }

    return theme === 'links' ? 'links' : 'default';
};

const AccordionRoot = ({
    children,
    allowMultipleExpanded = true,
    allowZeroExpanded = true,
    preExpanded,
    onChange,
    theme = 'default',
    variant,
    Icon = ArrowDownIcon,
    isIconVisible = true,
    animationType,
    transitionTimeout = 300,
    transitionTimeoutExit = transitionTimeout,
    onEnter,
    onEntering,
    onExit,
    className,
    ...props
}: AccordionProps) => {
    const [expandedIds, setExpandedIds] = useState<string[]>(preExpanded ?? []);
    const resolvedVariant = resolveVariant(theme, variant);

    const isItemExpanded = useCallback((itemId: string) => expandedIds.includes(itemId), [expandedIds]);

    const toggleItem = useCallback(
        (itemId: string) => {
            setExpandedIds(prev => {
                const isExpanded = prev.includes(itemId);

                if (isExpanded) {
                    if (!allowZeroExpanded && prev.length <= 1) {
                        return prev;
                    }

                    const next = prev.filter(id => id !== itemId);
                    onChange?.(next);

                    return next;
                }

                const next = allowMultipleExpanded ? [...prev, itemId] : [itemId];
                onChange?.(next);

                return next;
            });
        },
        [allowMultipleExpanded, allowZeroExpanded, onChange]
    );

    const contextValue = useMemo(
        () => ({
            expandedIds,
            toggleItem,
            isItemExpanded,
            animationType,
            transitionTimeout,
            transitionTimeoutExit,
            onEnter,
            onEntering,
            onExit,
            Icon,
            isIconVisible,
            variant: resolvedVariant,
        }),
        [
            expandedIds,
            toggleItem,
            isItemExpanded,
            animationType,
            transitionTimeout,
            transitionTimeoutExit,
            onEnter,
            onEntering,
            onExit,
            Icon,
            isIconVisible,
            resolvedVariant,
        ]
    );

    const rootClassName = cn(
        styles.accordion,
        resolvedVariant === 'links' ? styles.accordion_links : styles.accordion_default,
        className
    );

    return (
        <AccordionContext.Provider value={contextValue}>
            <div className={rootClassName} {...props}>
                {children}
            </div>
        </AccordionContext.Provider>
    );
};

export const Accordion = Object.assign(AccordionRoot, {
    Item: AccordionItem,
    Heading: AccordionHeading,
    Button: AccordionButton,
    Panel: AccordionPanel,
});

export default Accordion;
